/**
 * TrendBadge — compact delta arrow + formatted value (v0.5.8 glassboard kit).
 *
 * Honest by construction: a non-finite delta renders nothing. Zero renders
 * as a flat marker in muted tone (a true zero is information, unlike a
 * missing value).
 */

export interface TrendBadgeProps {
  /** Signed delta; sign drives arrow + tone. */
  delta: number;
  /** Pre-formatted display value; defaults to `+X.XX%`. */
  label?: string;
  className?: string;
}

export function TrendBadge({ delta, label, className = '' }: TrendBadgeProps) {
  if (!Number.isFinite(delta)) return null;

  const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const tone = dir === 'up' ? 'text-ok' : dir === 'down' ? 'text-bad' : 'text-ink-mute';
  const arrow = dir === 'up' ? '\u25b2' : dir === 'down' ? '\u25bc' : '\u25ac';
  const text = label ?? `${delta > 0 ? '+' : ''}${delta.toFixed(2)}%`;

  return (
    <span
      className={`num inline-flex items-center gap-1 text-xs ${tone} ${className}`.trim()}
      data-testid="trend-badge"
      data-trend={dir}
    >
      <span aria-hidden="true" className="text-[9px]">{arrow}</span>
      {text}
    </span>
  );
}
